import { Suspense } from 'react';

import LoadingSpinner from '../components/LoadingSpinner';
import { lazyWithReload } from '../utils/lazyWithReload';

const PageLoader = () => <LoadingSpinner fullScreen />;

export const LazyRoute = ({
  Component,
  ...props
}: {
  Component: React.LazyExoticComponent<React.ComponentType<any>>;
  [key: string]: any;
}) => (
  <Suspense fallback={<PageLoader />}>
    <Component {...props} />
  </Suspense>
);

// ---- Core pages -----------------------------------------------------------
export const Home = lazyWithReload(() =>
  import('../pages/home/Home').then((m) => ({ default: m.Home }))
);
export const MyPlan = lazyWithReload(() =>
  import('../pages/myPlan/MyPlan').then((m) => ({ default: m.MyPlan }))
);
export const DeepDivePicker = lazyWithReload(() =>
  import('../pages/myPlan/DeepDivePicker').then((m) => ({ default: m.DeepDivePicker }))
);
export const MyAdvisoryRequests = lazyWithReload(() =>
  import('../pages/myAdvisoryRequests/MyAdvisoryRequests').then((m) => ({
    default: m.MyAdvisoryRequests,
  }))
);
export const Contact = lazyWithReload(() =>
  import('../pages/contact/Contact').then((m) => ({ default: m.Contact }))
);
export const Project = lazyWithReload(() =>
  import('../pages/project/Project').then((m) => ({ default: m.Project }))
);
export const Search = lazyWithReload(() =>
  import('../pages/search/Search').then((m) => ({ default: m.Search }))
);
export const SupplyLink = lazyWithReload(() =>
  import('../pages/supplylink/SupplyLink').then((m) => ({ default: m.SupplyLink }))
);
export const WhereToBuy = lazyWithReload(() =>
  import('../pages/whereToBuy/WhereToBuy').then((m) => ({ default: m.WhereToBuy }))
);

// ---- Project sub-pages ----------------------------------------------------
export const EnvironmentalAndSocialRisks = lazyWithReload(() =>
  import('../pages/project/environmental_and_social_risks/EnvironmentalAndSocialRisks').then(
    (m) => ({ default: m.EnvironmentalAndSocialRisks })
  )
);
export const ExecutiveSummary = lazyWithReload(() =>
  import('../pages/project/executive_summary/ExecutiveSummary').then((m) => ({
    default: m.ExecutiveSummary,
  }))
);
export const GhgRating = lazyWithReload(() =>
  import('../pages/project/ghg_rating/GHGRating').then((m) => ({ default: m.GhgRating }))
);
export const RelatedContent = lazyWithReload(() =>
  import('../pages/project/RelatedContent').then((m) => ({ default: m.RelatedContent }))
);
export const SDGRating = lazyWithReload(() =>
  import('../pages/project/sdg_rating/SDGRating').then((m) => ({ default: m.SDGRating }))
);
export const PricingAndSupply = lazyWithReload(() =>
  import('../pages/project/pricing_and_supply/PricingAndSupply').then((m) => ({
    default: m.PricingAndSupply,
  }))
);

// ---- Retiree --------------------------------------------------------------
export const RetireeInsights = lazyWithReload(() =>
  import('../pages/retireeInsights/RetireeInsights').then((m) => ({ default: m.RetireeInsights }))
);
export const RetireeInsightsDrillDown = lazyWithReload(() =>
  import('../pages/retireeDrillDown/RetireeInsightsDrillDown').then((m) => ({
    default: m.RetireeInsightsDrillDown,
  }))
);

// ---- Insights Hub ---------------------------------------------------------
export const InsightsHub = lazyWithReload(() =>
  import('../pages/insightsHub/InsightsHub').then((m) => ({ default: m.InsightsHub }))
);
export const InsightsHubArticle = lazyWithReload(() =>
  import('../pages/insightsHubArticle/InsightsHubArticle').then((m) => ({
    default: m.InsightsHubArticle,
  }))
);
export const InsightsHubCategory = lazyWithReload(() =>
  import('../pages/insightsHubCategory/InsightsHubCategory').then((m) => ({
    default: m.InsightsHubCategory,
  }))
);
export const InsightsHubPinnedPost = lazyWithReload(() =>
  import('../pages/insightsHubArticle/InsightsHubPinnedPost').then((m) => ({
    default: m.InsightsHubPinnedPost,
  }))
);
export const InsightsHubResearchUpdate = lazyWithReload(() =>
  import('../pages/insightsHubResearchUpdate/InsightsHubResearchUpdate').then((m) => ({
    default: m.InsightsHubResearchUpdate,
  }))
);

// ---- Compass --------------------------------------------------------------
export const Compass = lazyWithReload(() =>
  import('../pages/compass/Compass').then((m) => ({ default: m.Compass }))
);
export const ProjectType = lazyWithReload(() =>
  import('../pages/compass/components/projectType/ProjectType').then((m) => ({
    default: m.ProjectType,
  }))
);
export const RootOverview = lazyWithReload(() =>
  import('../pages/compass/components/projectType/Renderer/Overview').then((m) => ({
    default: m.Overview,
  }))
);
export const RootSupplyAndDemand = lazyWithReload(() =>
  import('../pages/compass/components/projectType/Renderer/SupplyAndDemand').then((m) => ({
    default: m.SupplyAndDemand,
  }))
);
export const RootTopGHGRisks = lazyWithReload(() =>
  import('../pages/compass/components/projectType/Renderer/TopGHGRisks').then((m) => ({
    default: m.TopGHGRisks,
  }))
);
export const RootTopGHGRisksDetail = lazyWithReload(() =>
  import('../pages/compass/components/projectType/Renderer/TopGHGRisksDetail').then((m) => ({
    default: m.TopGHGRisksDetail,
  }))
);
export const RootMethodologyOverview = lazyWithReload(() =>
  import('../pages/compass/components/projectType/Renderer/methodologyDetail/Overview').then(
    (m) => ({ default: m.Overview })
  )
);
export const MethodologyDetail = lazyWithReload(() =>
  import('../pages/compass/components/projectType/MethodologyDetail').then((m) => ({
    default: m.MethodologyDetail,
  }))
);
export const RootMethodologyArea = lazyWithReload(() =>
  import('../pages/compass/components/projectType/Renderer/methodologyDetail/MethodologyArea').then(
    (m) => ({ default: m.MethodologyArea })
  )
);
export const RootScope = lazyWithReload(() =>
  import('../pages/compass/components/projectType/Renderer/methodologyDetail/Scope').then((m) => ({
    default: m.Scope,
  }))
);
export const RootCorsia = lazyWithReload(() =>
  import(
    '../pages/compass/components/projectType/Renderer/methodologyDetail/CorsiaEligibility'
  ).then((m) => ({ default: m.CorsiaEligibility }))
);

// ---- Project Screener -----------------------------------------------------
export const ProjectScreener = lazyWithReload(() =>
  import('../pages/projectScreener/ProjectScreener').then((m) => ({
    default: m.ProjectScreener,
  }))
);
export const NewScreening = lazyWithReload(() =>
  import('../pages/projectScreener/NewScreening').then((m) => ({ default: m.NewScreening }))
);
export const ScreeningResults = lazyWithReload(() =>
  import('../pages/projectScreener/ScreeningResults').then((m) => ({
    default: m.ScreeningResults,
  }))
);
export const ScreenerFAQ = lazyWithReload(() =>
  import('../pages/projectScreener/ScreenerFAQ').then((m) => ({ default: m.ScreenerFAQ }))
);

// ---- Auth / Misc ----------------------------------------------------------
export const SignUp = lazyWithReload(() =>
  import('../pages/signup/SignUp').then((m) => ({ default: m.SignUp }))
);
export const Register = lazyWithReload(() =>
  import('../pages/register/Register').then((m) => ({ default: m.Register }))
);
export const AccountRequest = lazyWithReload(() =>
  import('../pages/accountRequest/AccountRequest').then((m) => ({ default: m.AccountRequest }))
);
export const DeveloperPortal = lazyWithReload(() =>
  import('../pages/developerPortal/DeveloperPortal').then((m) => ({ default: m.DeveloperPortal }))
);
export const Upgrade = lazyWithReload(() =>
  import('../pages/upgrade/Upgrade').then((m) => ({ default: m.Upgrade }))
);
export const Programs = lazyWithReload(() =>
  import('../pages/programs/Programs').then((m) => ({ default: m.Programs }))
);
export const FreeDashboard = lazyWithReload(() => import('../pages/freeDashboard/FreeDashboard'));
export const ExpertAdvisory = lazyWithReload(() =>
  import('../pages/expertAdvisory/ExpertAdvisory').then((m) => ({ default: m.ExpertAdvisory }))
);
export const ExpertAdvisoryLearnMore = lazyWithReload(() =>
  import('../pages/expertAdvisory/ExpertAdvisoryLearnMore').then((m) => ({
    default: m.ExpertAdvisoryLearnMore,
  }))
);

// ---- Demo (magic link) ----------------------------------------------------
export const Demo = lazyWithReload(() => import('../pages/demo/Demo'));
